import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { useSelector, useDispatch } from 'react-redux';
import { contestSubmissionsSelector } from '../../redux/contest/selectors';
import { MetaTags } from '../../components/Metatags/Metatags';
import { getContestSubmissions } from '../../redux/contest/actions';
import { usePagination } from '../../hook/usePagination';
import { ContestSubmissionsContainer } from '../../components/ScreenContainer/ContestSubmissionsContainer/ContestSubmissionsContainer';
import { IProject } from '../../types/global/helper';
import { setmedia } from '../../redux/project/actions';
import { verifyPaystackPurchase } from '../../redux/payment/action';
import { getTokenBalance } from '../../redux/token/actions';
import { setContestInFocusForTokens, setVotingFlow } from '../../redux/contest/actions';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { navigate } from '../../redux/navigator/action';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { ROUTES } from '../../types/global/routes.types';

const ContestSubmissions = () => {
    const params = useParams();
    const dispatchPromise = useDispatchPromise();
    const dispatch = useDispatch();
    const { contestId } = params;
    const [searchParams, setSearchParams] = useSearchParams();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const [paystackStatus, setPaystackStatus] = useState<'verifying' | null>(null);
    const paystackHandled = useRef(false);
    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const [sortOption, setSortOption] = useState<string>('date');
    const [orderOption, setOrderOption] = useState<'asc' | 'desc'>('desc');
    const { pagination, setPagination, handleRowsChange, handlePageChange } = usePagination();

    // Get data from redux store
    const {
        contestSubmissions,
        loadingSubmissions,
        searchingSubmissions,
        submissionsError,
        submissionsContest,
        availableFilters,
    } = useSelector(contestSubmissionsSelector);

    // Fetch contest details and submissions
    const getData = useCallback(async () => {
        if (contestId) {
            // Then get the submissions
            const response: any = await dispatchPromise(
                getContestSubmissions({
                    contestId,
                    page: pagination.page,
                    limit: pagination.rows,
                    sort: sortOption as any,
                    order: orderOption,
                    ...(!!searchTerm && { searchTerm: searchTerm }),
                }),
            );

            const { pageSize, totalCount, currentPage } = response;

            setPagination({
                page: Number(currentPage),
                rows: pageSize,
                totalCount: totalCount,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contestId, pagination.page, pagination.rows, sortOption, orderOption, searchTerm]);

    // Initial data fetch
    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        const reference = searchParams.get('reference') ?? searchParams.get('trxref');
        if (!reference || paystackHandled.current) return;
        paystackHandled.current = true;
        setSearchParams({}, { replace: true });

        const run = async () => {
            setPaystackStatus('verifying');
            try {
                await dispatchPromise(verifyPaystackPurchase({ reference }));
                // Small delay so backend has time to credit the tokens before we fetch balance
                await new Promise(r => setTimeout(r, 1500));
                dispatch(getTokenBalance({}));
                showToastMessage('Payment confirmed. Tokens added to your balance.', 'success');

                // Reopen the TokenPurchaseModal if we know which contest triggered this
                try {
                    const session = JSON.parse(sessionStorage.getItem('generic_paystack_session') ?? '{}');
                    const storedContestId = session?.contestId ?? contestId;
                    if (storedContestId) {
                        dispatch(setContestInFocusForTokens(storedContestId));
                        dispatch(setVotingFlow({ submissions: [], showVotingModal: true }));
                    }
                } catch {
                    /* ignore */
                }
            } catch (err: any) {
                const msg = err?.data?.message ?? err?.message ?? 'Could not verify payment.';
                showToastMessage(msg, 'error');
            } finally {
                setPaystackStatus(null);
                try {
                    sessionStorage.removeItem('generic_paystack_session');
                } catch {
                    /* ignore */
                }
            }
        };
        void run();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle search input change
    const handleSearchChange = (searchText: string) => {
        setSearchTerm(searchText);
        // Reset to first page when searching
        if (pagination.page !== 1) {
            setPagination(prev => ({
                ...prev,
                page: 1,
            }));
        }
    };

    // Handle sort option change
    const handleSortOptionChange = (option: string) => {
        setSortOption(option);
        // Reset to first page when changing sort
        if (pagination.page !== 1) {
            setPagination(prev => ({
                ...prev,
                page: 1,
            }));
        }
    };

    // Handle order change
    const handleOrderChange = (order: 'asc' | 'desc') => {
        setOrderOption(order);
        // Reset to first page when changing order
        if (pagination.page !== 1) {
            setPagination(prev => ({
                ...prev,
                page: 1,
            }));
        }
    };

    // Handle project play (view project details)
    const handlePlayProject = (project: IProject) => {
        dispatch(
            setmedia({
                media: project,
                mediaType: 'act',
            }),
        );
    };

    const handleBackToContest = useCallback(() => {
        const contestKey = submissionsContest?.slug || submissionsContest?.contest_id || contestId;
        if (!contestKey) return;
        const route = replaceRouteParam(ROUTES.ESVIEW_CONTEST, 'contestId', String(contestKey));
        dispatch(navigate({ routes: route, skipAuthentication: true }));
    }, [submissionsContest, contestId, dispatch]);

    return (
        <MetaTags
            title={`Submissions | ${submissionsContest?.title || 'Contest'}`}
            description={submissionsContest?.description || ''}>
            {paystackStatus && (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 9999,
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                    }}>
                    <CircularProgress sx={{ color: t.primary_orange }} size={48} />
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: t.primary_white }}>
                        Confirming your payment…
                    </Typography>
                </Box>
            )}
            <ContestSubmissionsContainer
                contest={submissionsContest}
                submissions={contestSubmissions}
                loading={loadingSubmissions}
                searching={searchingSubmissions}
                error={submissionsError}
                pagination={pagination}
                searchTerm={searchTerm}
                sortOption={sortOption}
                orderOption={orderOption}
                availableFilters={availableFilters || []}
                onSearchChange={handleSearchChange}
                onSortOptionChange={handleSortOptionChange}
                onOrderChange={handleOrderChange}
                onRowsChange={handleRowsChange}
                onPageChange={handlePageChange}
                onPlay={handlePlayProject}
                onClearSearch={() => {
                    setSearchTerm(undefined);
                    getData();
                }}
                onBackButtonPressed={handleBackToContest}
            />
        </MetaTags>
    );
};

export { ContestSubmissions };
